import type { JSX } from "react";

import { AlertTriangle, CheckCircle2, Info } from "lucide-react";

import { ALL, useRecommendationFilters } from "../hooks/useRecommendationFilters";
import type { RecommendationFilterSpec } from "../hooks/useRecommendationFilters";
import type { DisplacementAction, VisibilityExplanation } from "../types/visibility";
import { PRIORITY_OPTIONS } from "../utils/priority";
import {
  RECOMMENDATION_TYPE_OPTIONS,
  SCORING_CATEGORY_OPTIONS,
} from "../utils/recommendationLabels";
import Chip from "./Chip";
import RecommendationEmptyState from "./RecommendationEmptyState";
import RecommendationFilterBar from "./RecommendationFilterBar";
import VisibilityActionCard from "./VisibilityActionCard";
import VisibilitySourceMix from "./VisibilitySourceMix";

/**
 * One descriptor per dropdown, in the same vocabulary the AI Audit Recommendations tab filters on —
 * `DisplacementAction` is typed to share those exact values (see `types/visibility.ts`), so the
 * same filter bar applies unchanged. No Status or Effort axis: a displacement action has neither a
 * completion state nor a costed effort band.
 */
const ACTION_FILTERS: RecommendationFilterSpec<DisplacementAction>[] = [
  {
    key: "priority",
    label: "Priority",
    allLabel: "All Priorities",
    options: PRIORITY_OPTIONS,
    matches: (action, value) => value === ALL || action.impact === value,
    valueOf: (action) => action.impact,
  },
  {
    key: "type",
    label: "Type",
    allLabel: "All Types",
    options: RECOMMENDATION_TYPE_OPTIONS,
    matches: (action, value) => value === ALL || action.recommendation_type === value,
    valueOf: (action) => action.recommendation_type,
  },
  {
    key: "scoringCategory",
    label: "Scoring Category",
    allLabel: "All Categories",
    options: SCORING_CATEGORY_OPTIONS,
    matches: (action, value) => value === ALL || action.scoring_category === value,
    valueOf: (action) => action.scoring_category,
  },
];

interface VisibilityWhyPanelProps {
  explanation: VisibilityExplanation | null | undefined;
  userBusinessName: string | null;
}

const openBusinessProfile = () => window.dispatchEvent(new CustomEvent("openBusinessProfile"));

/**
 * The "Why These Results" tab body. Walks the user from what happened to what to do, in a fixed
 * order — never flatten `retrieved` / `parametric` / `unattributed` into one narrative.
 */
const VisibilityWhyPanel = ({
  explanation,
  userBusinessName,
}: VisibilityWhyPanelProps): JSX.Element => {
  // Called unconditionally, ahead of the early return below, so the hook order never changes
  // between a report with a "why" breakdown and one without.
  const actionFilters = useRecommendationFilters(explanation?.actions ?? [], ACTION_FILTERS);

  if (!explanation) {
    return (
      <div className="text-center py-16">
        <Info className="w-10 h-10 text-on-surface-variant mx-auto mb-4" />
        <p className="text-on-surface-variant">
          A "why" breakdown isn't available for this report.
        </p>
      </div>
    );
  }

  const {
    confidence,
    confidence_note,
    providers,
    source_type_counts,
    actions,
    user_is_ranked,
    user_site_cited,
    evidence_degraded,
  } = explanation;

  const hasSourceMix = Object.keys(source_type_counts || {}).length > 0;

  const renderWhereYouStand = () => {
    if (!userBusinessName) {
      return (
        <button
          onClick={openBusinessProfile}
          className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-container-high bg-surface-container-low text-left hover:bg-surface-container-high transition-colors"
        >
          <Info className="w-5 h-5 text-primary shrink-0" />
          <span className="text-sm text-on-surface-variant">
            Complete your business profile to see where{" "}
            <span className="text-on-surface font-medium">your business</span> stands in this
            breakdown.
          </span>
        </button>
      );
    }

    const message = user_is_ranked
      ? user_site_cited
        ? `${userBusinessName} is ranked in these results, and your own site was cited as a source.`
        : `${userBusinessName} is ranked in these results, though none of the cited sources point to your own site.`
      : user_site_cited
        ? `${userBusinessName} isn't ranked in these results yet, even though your own site was cited as a source.`
        : `${userBusinessName} isn't ranked in these results, and your own site wasn't cited as a source.`;

    const Icon = user_is_ranked ? CheckCircle2 : AlertTriangle;

    return (
      <div className="flex items-start gap-3 p-4 rounded-xl border border-surface-container-high bg-surface-container-lowest">
        <Icon
          className={`w-5 h-5 shrink-0 mt-0.5 ${user_is_ranked ? "text-emerald-500" : "text-amber-400"}`}
        />
        <p className="text-sm text-on-surface-variant">{message}</p>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Confidence strip — always rendered, never collapsed or behind an info icon */}
      <div className="flex items-start gap-3 p-4 rounded-xl border border-surface-container-high bg-surface-container-low">
        <Chip color={confidence === "moderate" ? "blue" : "neutral"} className="shrink-0">
          {confidence === "moderate" ? "Moderate confidence" : "Low confidence"}
        </Chip>
        <p className="text-sm text-on-surface-variant">{confidence_note}</p>
      </div>

      {/* What the engines read */}
      {hasSourceMix && (
        <div>
          <h4 className="text-sm font-bold text-on-surface mb-3">What the engines read</h4>
          <VisibilitySourceMix sourceTypeCounts={source_type_counts} providers={providers} />
        </div>
      )}

      {/* Where you stand */}
      <div>
        <h4 className="text-sm font-bold text-on-surface mb-3">Where you stand</h4>
        {renderWhereYouStand()}
      </div>

      {/* What to do */}
      <div>
        <h4 className="text-sm font-bold text-on-surface mb-3">What to do</h4>
        {actions.length > 0 ? (
          <>
            <RecommendationFilterBar
              filters={ACTION_FILTERS}
              values={actionFilters.values}
              options={actionFilters.options}
              hasActiveFilter={actionFilters.hasActiveFilter}
              onChange={actionFilters.setFilter}
              onClear={actionFilters.clearFilters}
              shownCount={actionFilters.filtered.length}
              totalCount={actions.length}
              noun="actions"
            />
            {actionFilters.filtered.length === 0 ? (
              <RecommendationEmptyState
                totalCount={actions.length}
                hasActiveFilter={actionFilters.hasActiveFilter}
                emptyMessage="No specific levers identified from this run."
                onClear={actionFilters.clearFilters}
              />
            ) : (
              <div className="space-y-4">
                {actionFilters.filtered.map((action, i) => (
                  <VisibilityActionCard key={`${action.title}-${i}`} action={action} />
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-on-surface-variant">
            No specific levers identified from this run.
          </p>
        )}
      </div>

      {/* Degraded note — a limitation, not a fault, so no error palette */}
      {evidence_degraded && (
        <p className="text-sm text-on-surface-variant italic">
          Per-business attribution is partial for this report — some evidence could not yet be tied
          to a specific business.
        </p>
      )}
    </div>
  );
};

export default VisibilityWhyPanel;
